import axios, { AxiosInstance } from "axios";
import API from "../Utils/API/API";
import workingTemplatePolicyRoutes from "../routes/working-template-policy-routes";
import {
  PutWorkingTemplatePolicyBindingRequest,
  WorkingTemplatePolicyApi,
  WorkingTemplatePolicyBinding,
  WorkingTemplatePolicyFailure,
  WorkingTemplatePolicySuccess
} from "./working-template-policy-api.types";

export class WorkingTemplatePolicyApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly retryable: boolean,
    readonly status?: number,
    readonly networkUnknown = false
  ) {
    super(message);
    this.name = "WorkingTemplatePolicyApiError";
  }
}

function positiveInteger(value: unknown): number | null {
  return typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value > 0
    ? value
    : null;
}

function normalizeBinding(value: unknown): WorkingTemplatePolicyBinding {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new WorkingTemplatePolicyApiError(
      "WORKING_TEMPLATE_POLICY_INVALID_RESPONSE",
      "서버의 근무 정책 연결 응답을 확인하지 못했습니다.",
      true
    );
  }
  const source = value as Record<string, unknown>;
  const workingTemplateIdx = positiveInteger(source.workingTemplateIdx);
  const workType = source.workType;
  const workPolicyId = source.workPolicyId;
  if (
    workingTemplateIdx === null ||
    typeof workType !== "number" ||
    !Number.isSafeInteger(workType) ||
    (workPolicyId !== null && positiveInteger(workPolicyId) === null)
  ) {
    throw new WorkingTemplatePolicyApiError(
      "WORKING_TEMPLATE_POLICY_INVALID_RESPONSE",
      "서버의 근무 정책 연결 응답을 확인하지 못했습니다.",
      true
    );
  }
  return {
    workingTemplateIdx,
    workType,
    workPolicyId: workPolicyId === null ? null : Number(workPolicyId)
  };
}

async function execute(
  request: Promise<{ data: WorkingTemplatePolicySuccess }>
): Promise<WorkingTemplatePolicySuccess> {
  try {
    const response = await request;
    if (!response.data || response.data.ok !== true) {
      throw new WorkingTemplatePolicyApiError(
        "WORKING_TEMPLATE_POLICY_INVALID_RESPONSE",
        "서버의 근무 정책 연결 응답을 확인하지 못했습니다.",
        true
      );
    }
    return {
      ...response.data,
      data: normalizeBinding(response.data.data)
    };
  } catch (error) {
    if (error instanceof WorkingTemplatePolicyApiError) {
      throw error;
    }
    if (axios.isCancel(error)) {
      throw new WorkingTemplatePolicyApiError(
        "WORKING_TEMPLATE_POLICY_ABORTED",
        "근무 정책 연결 요청이 취소되었습니다.",
        false
      );
    }
    if (axios.isAxiosError(error) && error.response) {
      const failure = error.response.data as WorkingTemplatePolicyFailure;
      if (failure && failure.ok === false && typeof failure.code === "string") {
        throw new WorkingTemplatePolicyApiError(
          failure.code,
          failure.message,
          failure.retryable,
          error.response.status
        );
      }
    }
    throw new WorkingTemplatePolicyApiError(
      "WORKING_TEMPLATE_POLICY_NETWORK_UNKNOWN",
      "서버 응답을 확인하지 못했습니다.",
      true,
      undefined,
      true
    );
  }
}

export function createWorkingTemplatePolicyApi(
  client: AxiosInstance
): WorkingTemplatePolicyApi {
  return {
    getBasePolicyBinding: (workingTemplateIdx, signal) =>
      execute(
        client.get(workingTemplatePolicyRoutes.binding(workingTemplateIdx), {
          signal
        })
      ),
    putBasePolicyBinding: (workingTemplateIdx, request, signal) =>
      execute(
        client.put(
          workingTemplatePolicyRoutes.binding(workingTemplateIdx),
          request,
          { signal }
        )
      )
  };
}

const workingTemplatePolicyApi = createWorkingTemplatePolicyApi(API());

export default workingTemplatePolicyApi;
export * from "./working-template-policy-api.types";
